import cuid from 'cuid';
import { Vec3 } from 'ayvri-std/lib/vector';
import { cartographicToCartesian } from 'ayvri-std/lib/ellipsoid';
import { defaultValue, convertPoint } from './helpers';

import Image from './media/Image';
import Photo from './media/Photo';

const MediaConstructors = {
  image: Image,
  photo: Photo,
};

class MediaCollection {
  constructor({ hud, terrainProfile, events, store, trackLoader, options }) {
    this.trackLoader = trackLoader;
    this.headless = options.headless;
    this.media = {};

    // This object is passed to the onReady() and destroy() methods of each MediaObject
    this.mediaContext = { hud, events, store, terrainProfile, media: this.media };

    this.deferredTargets = [];
  }

  onSceneLoad(scene) {
    // Add any Media specified by the scene
    scene.media.forEach(m => {
      this.add(m);
    });
  }

  update() {
    const mediaIds = Object.keys(this.media);
    for (let i = 0; i < mediaIds.length; i++) {
      const media = this.media[mediaIds[i]];
      if (media.ready && media.obj.update) media.obj.update(this.mediaContext);
    }
  }

  onPointsAdded(track) {
    // Find any MediaObjects whose positions can now be resolved
    const targets = this.deferredTargets.filter(d => d.activityId === track.id && d.time <= track.time.last);

    for (let i = 0; i < targets.length; i++) {
      const target = targets[i];

      // Query the track for its position at the specified time
      const posXYZ = { x: 0.0, y: 0.0, z: 0.0 };
      track.sampleLinear({ time: target.time }, posXYZ);

      // Resolve the deferred position promise for this MediaObject
      target.resolve(new Vec3(posXYZ.x, posXYZ.y, posXYZ.z));
    }

    // Remove the targets from deferred array
    this.deferredTargets = this.deferredTargets.filter(d => !targets.includes(d));
  }

  // @NOTE: Options must conform to the MediaSchema from Vera
  add(options) {
    // If a mediaId was not specified, generate a unique one
    const mediaId = defaultValue(options.mediaId, cuid());
    if (this.media[mediaId]) throw new Error('The specified mediaId already exists');
    const mediaItem = { ...options, mediaId };

    // Attempt to create the specified MediaType
    const MediaConstructor = MediaConstructors[mediaItem.mediaType];
    if (!MediaConstructor) {
      console.warn(`Attempted to add unknown media type ${mediaItem.mediaType}. Ignoring...`);
      return null;
    }

    // Construct the Media object
    const media = {
      desc: mediaItem,
      obj: new MediaConstructor(mediaItem),
      ready: false,
    };

    // If in headless mode, don't bother loading
    if (!this.headless) {
      // Asynchronously load the media (e.g. downloading the image)
      const loadPromise = media.obj.load(mediaItem);

      // Asynchronously query the position of the media (e.g. wait for track data to load, or sample terrain height)
      const posPromise = resolvePosition(mediaItem, this.trackLoader, this.mediaContext.store, this.deferredTargets);

      // When the media is loaded and has a position, mark it as ready
      Promise.all([posPromise, loadPromise])
        .then(([pos]) => {
          media.ready = true;
          media.obj.onReady(pos, this.mediaContext);
        })
        .catch(error => {
          media.ready = false;
          console.warn(`Media item "${mediaId}" failed to load: ${error}`);
        });
    }

    // Add the media to our list
    this.media[mediaId] = media;
    return mediaId;
  }

  destroy(mediaId) {
    const media = this.media[mediaId];
    if (!media) throw new Error('The specified mediaId does not exist');

    if (media.obj.destroy && !this.headless) media.obj.destroy(this.mediaContext);
    delete this.media[mediaId];
  }
}

// Returns a promise which resolves with a cartesian position
function resolvePosition(mediaItem, trackLoader, store, deferredTargets) {
  return new Promise((resolve, reject) => {
    if (mediaItem.mediaType === 'photo') {
      const time = new Date(mediaItem.target.time).getTime();
      const activityId = mediaItem.target.activityId;
      const track = store.getState().tracks.tracks[activityId];

      // If the track has already loaded past the target time, determine its position.
      // Otherwise defer and try again during OnPointsAdded()
      if (track && track.time.last >= time) {
        const posXYZ = track.sampleLinear({ time }, { x: 0.0, y: 0.0, z: 0.0 });
        resolve(new Vec3(posXYZ.x, posXYZ.y, posXYZ.z));
      } else {
        deferredTargets.push({ activityId, time, resolve });
      }
    } else {
      const carto = convertPoint(mediaItem.points[0]);
      if (!carto.height) reject(new Error('The Image media type is deprecated, and must specify a height'));
      else resolve(cartographicToCartesian(carto.latitude, carto.longitude, carto.height));
    }
  });
}

export default MediaCollection;
